import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DeviceAttribution } from "@/components/DeviceAttribution";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, MapPin, Thermometer, Wind, Battery, AlertCircle, MessageCircle } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";
import type { LatLngBoundsExpression } from "leaflet";

import { Card, SectionLabel } from "@/components/ui/Card";
import { BackButton } from "@/components/ui/BackButton";
import { Button } from "@/components/ui/Button";
import { LoadError } from "@/components/ui/LoadError";
import { PrepareDetailsButton, PreparationReason } from "@/components/plan/PrepareDetailsButton";
import { getWorkout } from "@/api/workouts";
import { getPlannedWorkout } from "@/api/plan";
import { fmtLongDate } from "@/lib/dates";
import { plannedWorkoutTitle, workoutTypeLabel } from "@/lib/planned-workout-labels";
import { fmtDuration as fmtDurationMin, SPORT_LABELS } from "@/lib/tss";
import {
  formatDistance,
  formatDuration,
  formatElevation,
  formatPace,
  sportNameRu,
  zoneColor,
  zoneLabel,
  computeBounds,
  computeZ5PlusTime,
  feelLabel,
  msToKmh,
  totalZoneTime,
} from "@/lib/workout-formatting";
import type {
  HrZoneBin,
  PlannedWorkout,
  PowerZoneBin,
  WorkoutDetail as WorkoutDetailType,
  WorkoutLap,
} from "@/types";
import { INTERVAL_COLOR, MAP, SERIES, THEME, inkOn } from "@/lib/theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
);

interface LocState {
  week?: number;
}

export default function WorkoutDetail({
  plannedById = false,
}: {
  plannedById?: boolean;
} = {}) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const isNumericId = Number.isFinite(numericId) && numericId > 0;
  const { state } = useLocation() as { state: LocState };
  const nav = useNavigate();

  // Always re-authorize the record through the API. Old browser history may
  // contain a full workout belonging to a previously signed-in account.
  const plannedQ = useQuery({
    queryKey: ["planned-workout", numericId],
    queryFn: ({ signal }) => getPlannedWorkout(numericId, signal),
    enabled: plannedById && isNumericId,
  });

  // Otherwise it's an actual (completed) workout from /workout/:id.
  const actualQ = useQuery({
    queryKey: ["workout", numericId],
    queryFn: () => getWorkout(numericId),
    enabled: !plannedById && isNumericId,
  });

  if (plannedById) {
    if (plannedQ.isLoading) {
      return <div className="py-10 text-center text-taupe">{t("workout_detail.loading")}</div>;
    }
    const errorStatus = (plannedQ.error as { response?: { status?: number } } | null)?.response?.status;
    if (plannedQ.isError && errorStatus !== 404 && (!plannedQ.data || errorStatus === 401 || errorStatus === 403)) {
      return <LoadError onRetry={() => void plannedQ.refetch()} retrying={plannedQ.isFetching} />;
    }
    if (errorStatus === 404 || !plannedQ.data) {
      return (
        <div className="space-y-5">
          <BackButton to="/" label={t("workout_detail.back_home")} withHome={false} />
          <Card tone="cream">
            <p className="text-[14px] text-taupe">{t("workout_detail.planned_not_found")}</p>
          </Card>
          <Button variant="ghost" onClick={() => nav(-1)} className="w-full">
            {t("workout_detail.back")}
          </Button>
        </div>
      );
    }
    return <PlannedView workout={plannedQ.data} week={state?.week}
      onRefresh={() => void plannedQ.refetch()} refreshing={plannedQ.isFetching} refreshFailed={plannedQ.isError} />;
  }

  if (actualQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("workout_detail.loading")}</div>;
  }

  if (actualQ.error || !actualQ.data) {
    return (
      <div className="space-y-5">
        <BackButton to="/history" label={t("workout_detail.back_history")} />
        <Card tone="cream">
          <p className="text-[14px] text-taupe">{t("workout_detail.not_found")}</p>
        </Card>
        <Button variant="ghost" onClick={() => nav(-1)} className="w-full">
          {t("workout_detail.back")}
        </Button>
      </div>
    );
  }

  if (actualQ.data.source_unavailable) {
    return <div className="mx-auto max-w-3xl space-y-5">
      <BackButton to="/history" label={t("workout_detail.back_history")} />
      <h1 className="font-serif text-[26px] font-medium">{t("workout_detail.source_unavailable")}</h1>
      <p className="text-sm text-taupe">{fmtLongDate(actualQ.data.date)} · Strava · Intervals.icu</p>
      <Card><p className="text-sm text-navy-deep">{t("workout_detail.source_unavailable_note")}</p></Card>
    </div>;
  }
  return <ActualView workout={actualQ.data} onBack={() => nav(-1)} />;
}

// ============================================================================
// Planned workout view (kept largely unchanged from previous version)
// ============================================================================

function PlannedView({
  workout,
  week,
  onRefresh,
  refreshing,
  refreshFailed,
}: {
  workout: PlannedWorkout;
  week: number | undefined;
  onRefresh: () => void;
  refreshing: boolean;
  refreshFailed: boolean;
}) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const isPreview = workout.status === "skeleton" || (!!workout.detail_status && workout.detail_status !== "ready");
  const weekNumber = workout.week_number === undefined ? week : workout.week_number;
  const detailMessages: Partial<Record<NonNullable<PlannedWorkout["detail_status"]>, string>> = {
    preview_future: "workout_detail.preview_future_desc",
    preview_due: "workout_detail.preview_due_desc",
    preview_past: "workout_detail.preview_past_desc",
    blocked_source: "workout_detail.preview_source_desc",
    blocked_health: "planning_quality.health_body",
  };
  const statusMessage = workout.detail_status ? detailMessages[workout.detail_status] : undefined;
  const settingsRelevant = workout.detail_status === "blocked_source" || workout.detail_status === "blocked_health";
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <BackButton
        to={weekNumber ? `/plan/week/${weekNumber}` : "/plan"}
        label={weekNumber ? t("workout_detail.back_week") : t("workout_detail.back_plan")}
      />

      <div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber/15 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-amber-dark">
            {SPORT_LABELS[workout.sport] ?? workout.sport}
          </span>
          <span className="text-[12px] text-taupe">{workoutTypeLabel(workout.type)}</span>
          {isPreview ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-cream-border bg-cream-light px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-taupe">
              {t("workout_detail.preliminary")}
            </span>
          ) : null}
        </div>
        <h1 className="mt-1.5 font-serif text-[26px] font-medium leading-snug">
          {plannedWorkoutTitle(workout)}
        </h1>
        <div className="mt-1 text-[13px] text-taupe">
          {fmtLongDate(workout.date)} · {fmtDurationMin(workout.duration_minutes)} ·
          TSS {workout.target_tss}
        </div>
      </div>

      {isPreview ? (
        <div role="status" className="rounded-lg border border-amber/40 border-l-4 bg-amber/10 p-4">
          <div className="mb-1 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-dark" strokeWidth={2} />
            <span className="text-[12px] font-medium uppercase tracking-wider text-amber-dark">
              {t("workout_detail.preliminary")}
            </span>
          </div>
          <p className="text-[13px] leading-relaxed text-navy-deep">
            {t(statusMessage ?? "workout_detail.preliminary_desc")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {workout.can_prepare_details && weekNumber && workout.week_start_date && !refreshFailed &&
              <PrepareDetailsButton weekNumber={weekNumber} weekStart={workout.week_start_date} disabled={refreshing} />}
            <Button type="button" variant="secondary" onClick={onRefresh} disabled={refreshing}>
              {t(refreshing ? "workout_detail.checking_details" : "workout_detail.check_details")}
            </Button>
            {settingsRelevant && <Link to="/settings" className="text-sm font-medium text-amber-dark underline">{t("nav.settings")}</Link>}
          </div>
          {!workout.can_prepare_details && !settingsRelevant && <PreparationReason reason={workout.preparation_reason} />}
        </div>
      ) : null}

      {refreshFailed && <p role="alert" className="rounded-lg border border-coral/30 p-3 text-sm text-coral">{t("workout_detail.details_refresh_failed")}</p>}

      {!isPreview && Object.keys(workout.target_zones).length > 0 ? (
        <Card>
          <SectionLabel>{t("workout_detail.target_zones")}</SectionLabel>
          <dl className="mt-2 grid grid-cols-2 gap-2">
            {Object.entries(workout.target_zones).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-cream px-3 py-2">
                <dt className="text-[11px] uppercase tracking-wider text-taupe">{k}</dt>
                <dd className="metric-number text-[16px]">
                  {Array.isArray(v) ? (v as unknown[]).join(" – ") : String(v)}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      ) : null}

      {!isPreview && <Card tone="navy">
        <SectionLabel className="text-amber-light">{t("workout_detail.coach_instruction")}</SectionLabel>
        <div className="coach-voice mt-3 whitespace-pre-wrap">{workout.description}</div>
      </Card>}

      {!isPreview && workout.intervals.length > 0 ? (
        <IntervalsTimeline intervals={workout.intervals as IntervalStep[]} />
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          variant="secondary"
          onClick={() => nav("/chat")}
          className="flex-1 gap-2"
        >
          <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
          {t("workout_detail.ask_coach")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => nav(-1)}
          className="flex-1"
        >
          {t("workout_detail.back")}
        </Button>
      </div>
    </div>
  );
}

// ---- IntervalsTimeline -----------------------------------------------------

interface IntervalStep {
  type?: string;
  duration_min?: number;
  reps?: number;
  description?: string;
  target_zone?: string;
  target_power_pct_ftp?: string;
  target_pace_pct_threshold?: string;
  target_pace_per_100m?: string;
}



const INTERVAL_LABEL_SHORT: Record<string, string> = {
  warmup: "WU",
  cooldown: "CD",
  recovery: "REC",
  main: "Main",
  interval: "×",
};

const INTERVAL_LABEL_FULL: Record<string, string> = {
  warmup: "Warmup",
  cooldown: "Cooldown",
  recovery: "Recovery",
  main: "Main set",
  interval: "Interval",
};

function IntervalsTimeline({ intervals }: { intervals: IntervalStep[] }) {
  const { t } = useTranslation();
  const total = intervals.reduce(
    (s, i) => s + ((i.duration_min || 0) * (i.reps || 1)),
    0,
  );

  return (
    <Card>
      <SectionLabel>{t("workout_detail.structure")}</SectionLabel>

      {total > 0 ? (
        <div className="mt-3">
          <div className="flex h-8 overflow-hidden rounded-md bg-cream-border">
            {intervals.map((iv, idx) => {
              const minutes = (iv.duration_min || 0) * (iv.reps || 1);
              const pct = (minutes / total) * 100;
              const key = (iv.type || "main").toLowerCase();
              const color = INTERVAL_COLOR[key] || THEME.muted;
              const short = INTERVAL_LABEL_SHORT[key] || "?";
              return (
                <div
                  key={idx}
                  className="flex items-center justify-center text-[10px] font-medium text-white"
                  style={{ width: `${pct}%`, background: color, minWidth: 18 }}
                  title={`${iv.type || "step"} · ${minutes}min`}
                >
                  {iv.reps && iv.reps > 1 && key === "interval"
                    ? `${iv.reps}×${iv.duration_min}m`
                    : short}
                </div>
              );
            })}
          </div>
          <div className="mt-1 text-right text-[11px] text-taupe">
            {t("workout_detail.total_min", { n: total })}
          </div>
        </div>
      ) : null}

      <ul className="mt-4 space-y-2">
        {intervals.map((iv, idx) => {
          const key = (iv.type || "main").toLowerCase();
          const color = INTERVAL_COLOR[key] || THEME.muted;
          const label = INTERVAL_LABEL_FULL[key] || iv.type || "Step";
          const target =
            iv.target_power_pct_ftp
              ? `${iv.target_power_pct_ftp}% FTP`
              : iv.target_pace_pct_threshold
                ? `${iv.target_pace_pct_threshold}% pace`
                : iv.target_pace_per_100m
                  ? `${iv.target_pace_per_100m}/100m`
                  : iv.target_zone || "";
          return (
            <li
              key={idx}
              className="flex items-start gap-3 rounded-md border-l-2 bg-cream-light/50 p-3"
              style={{ borderLeftColor: color }}
            >
              <div className="w-16 shrink-0 text-[11px] font-medium uppercase tracking-wider text-taupe">
                {label}
              </div>
              <div className="min-w-0 flex-1">
                {iv.description ? (
                  <div className="text-[13px] leading-relaxed text-navy-deep">
                    {iv.description}
                  </div>
                ) : null}
                <div className="mt-0.5 text-[11px] text-taupe">
                  {iv.duration_min ? fmtDurationMin(iv.duration_min) : ""}
                  {iv.reps && iv.reps > 1 ? ` · ${t("workout_detail.reps", { n: iv.reps })}` : ""}
                  {target ? ` · ${target}` : ""}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <details className="mt-4">
        <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-taupe hover:text-navy-deep">
          {t("workout_detail.json_export")}
        </summary>
        <pre className="mt-2 overflow-auto rounded-md bg-cream p-3 text-[11px] text-navy-deep">
          {JSON.stringify(intervals, null, 2)}
        </pre>
      </details>
    </Card>
  );
}

// ============================================================================
// Actual workout view — rich Intervals.icu data
// ============================================================================

function ActualView({
  workout,
  onBack,
}: {
  workout: WorkoutDetailType;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const hasHrZones =
    workout.hr_zones.length > 0 &&
    workout.hr_zones.some((z) => (z.time_s || 0) > 0);
  const hasPowerZones =
    workout.power_zones.length > 0 &&
    workout.power_zones.some((z) => (z.time_s || 0) > 0);
  const hasLaps = workout.laps.length > 0 || workout.intervals.length > 0;
  const lapsToShow: WorkoutLap[] =
    workout.intervals.length > 0 ? workout.intervals : workout.laps;
  const streams = workout.streams;
  const hasChart =
    streams &&
    (streams.heartrate.some((v) => v !== null && v !== undefined) ||
      streams.watts.some((v) => v !== null && v !== undefined) ||
      streams.pace_s_per_km.some((v) => v !== null && v !== undefined));
  const hasMap = streams && streams.latlng && streams.latlng.length > 1;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <BackButton to="/history" label="К истории" />

      {/* ==================== Hero ==================== */}
      <HeroCard workout={workout} />

      {/* ==================== Time-series chart ==================== */}
      {hasChart ? <StreamsChart workout={workout} /> : null}

      {/* ==================== Laps / Intervals table ==================== */}
      {hasLaps ? (
        <LapsTable laps={lapsToShow} label={workout.intervals.length > 0 ? t("workout_detail.intervals") : t("workout_detail.laps")} sport={workout.sport} />
      ) : null}

      {/* ==================== Two-column zones row (desktop) ==================== */}
      {(hasHrZones || hasPowerZones) && (
        <div className="grid gap-5 md:grid-cols-2">
          {hasHrZones ? <HrZonesCard zones={workout.hr_zones} /> : null}
          {hasPowerZones ? <PowerZonesCard zones={workout.power_zones} /> : null}
        </div>
      )}

      {/* ==================== Map ==================== */}
      {hasMap ? <RouteMap latlng={streams.latlng} /> : null}

      {/* ==================== Analysis ==================== */}
      <AnalysisCard workout={workout} />

      {/* ==================== Weather + device ==================== */}
      <MetaRow workout={workout} />

      {/* ==================== Description ==================== */}
      {workout.description ? (
        <Card tone="navy">
          <SectionLabel className="text-amber-light">{t("workout_detail.description")}</SectionLabel>
          <p className="coach-voice mt-3 whitespace-pre-wrap">{workout.description}</p>
        </Card>
      ) : null}

      {/* ==================== Actions ==================== */}
      <div className="flex flex-wrap gap-3">
        {workout.external_url ? (
          <a
            href={workout.external_url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-lg bg-amber px-4 py-2.5 text-[14px] font-medium text-navy hover:bg-amber-dark"
          >
            {t("workout_detail.open_in_intervals_icu")}
            <ExternalLink className="h-4 w-4" strokeWidth={1.8} />
          </a>
        ) : null}
        <Button variant="ghost" onClick={onBack}>
          Назад
        </Button>
      </div>

      {/* ==================== Raw JSON ==================== */}
      <details className="rounded-2xl border border-cream-border bg-cream-light p-4">
        <summary className="cursor-pointer text-[12px] font-medium uppercase tracking-wider text-taupe">
          {t("workout_detail.raw_json", { n: workout.raw_data_field_count })}
        </summary>
        <pre className="mt-3 overflow-auto rounded-lg bg-cream p-3 text-[11px] text-navy-deep">
          {JSON.stringify(workout.raw_data, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ============================================================================
// Hero — primary identity + headline metrics
// ============================================================================

function HeroCard({ workout }: { workout: WorkoutDetailType }) {
  const { t } = useTranslation();
  const ifNumber =
    workout.intensity_factor_pct !== null
      ? Math.round(workout.intensity_factor_pct)
      : null;
  const avgKmh = msToKmh(workout.avg_speed_ms);

  return (
    <div className="rounded-2xl bg-navy p-6 text-cream-light shadow-soft md:p-8">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[1.5px] text-amber font-medium flex flex-wrap items-center gap-2">
          <span>{sportNameRu(workout.sport)} · {fmtLongDate(workout.date)}</span>
          {workout.device_name ? (
            <DeviceAttribution deviceName={workout.device_name} compact />
          ) : null}
        </div>
        <h1 className="mt-1 font-serif text-[26px] font-medium leading-tight text-cream md:text-[32px]">
          {workout.title}
        </h1>
        {workout.external_id ? (
          <div className="mt-1 font-mono text-[11px] text-cream-light">
            #{workout.external_id}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-4 md:grid-cols-4">
        <HeroMetric label={t("workout_detail.duration")} value={formatDuration(workout.duration_s)} />
        <HeroMetric
          label={t("workout_detail.distance")}
          value={workout.distance_km !== null ? `${workout.distance_km} ${t("workout_detail.km")}` : "—"}
        />
        <HeroMetric
          label="TSS"
          value={workout.tss !== null ? Math.round(workout.tss).toString() : "—"}
          highlight
        />
        {ifNumber !== null ? (
          <HeroMetric label={t("workout_detail.intensity")} value={`${ifNumber}%`} />
        ) : (
          <HeroMetric
            label={t("workout_detail.elevation")}
            value={formatElevation(workout.elevation_gain_m)}
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-sm md:grid-cols-6">
        {workout.avg_hr !== null ? (
          <SecondaryMetric label="HR avg" value={`${workout.avg_hr}`} unit="bpm" />
        ) : null}
        {workout.max_hr !== null ? (
          <SecondaryMetric label="HR max" value={`${workout.max_hr}`} unit="bpm" />
        ) : null}
        {workout.avg_power !== null ? (
          <SecondaryMetric
            label="Power"
            value={`${Math.round(workout.avg_power)}`}
            unit="W"
          />
        ) : null}
        {workout.normalized_power !== null ? (
          <SecondaryMetric
            label="NP"
            value={`${workout.normalized_power}`}
            unit="W"
          />
        ) : null}
        {workout.avg_pace_s_per_km !== null ? (
          <SecondaryMetric
            label={t("workout_detail.pace")}
            value={formatPace(workout.avg_pace_s_per_km)}
          />
        ) : null}
        {avgKmh !== null ? (
          <SecondaryMetric label={t("workout_detail.avg")} value={`${avgKmh.toFixed(1)}`} unit={t("workout_detail.kmh")} />
        ) : null}
        {workout.elevation_gain_m !== null ? (
          <SecondaryMetric
            label="Набор"
            value={Math.round(workout.elevation_gain_m).toString()}
            unit={t("workout_detail.m_short")}
          />
        ) : null}
        {workout.calories !== null ? (
          <SecondaryMetric
            label={t("workout_detail.calories")}
            value={workout.calories.toString()}
            unit="kcal"
          />
        ) : null}
        {workout.avg_cadence !== null ? (
          <SecondaryMetric
            label={t("workout_detail.cadence")}
            value={Math.round(workout.avg_cadence).toString()}
          />
        ) : null}
        {workout.feel !== null ? (
          <SecondaryMetric label="Feel" value={`${workout.feel}/5`} />
        ) : null}
        {workout.rpe !== null ? <SecondaryMetric label="RPE" value={`${workout.rpe}`} /> : null}
      </div>
    </div>
  );
}

function HeroMetric({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-medium uppercase tracking-[1.5px] text-amber">
        {label}
      </div>
      <div
        className={
          "metric-number font-medium text-cream " +
          (highlight
            ? "text-[28px] md:text-[32px]"
            : "text-[24px] md:text-[28px]")
        }
      >
        {value}
      </div>
    </div>
  );
}

function SecondaryMetric({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-[1.5px] text-cream-light">
        {label}
      </div>
      <div className="metric-number mt-0.5 text-[18px] font-medium text-cream">
        {value}
        {unit ? (
          <span className="ml-1 text-[12px] text-cream-light">{unit}</span>
        ) : null}
      </div>
    </div>
  );
}

// ============================================================================
// Streams chart — HR / Power / Pace overlaid on time axis
// ============================================================================

function StreamsChart({ workout }: { workout: WorkoutDetailType }) {
  const { t } = useTranslation();
  const s = workout.streams!;
  const hasHr = s.heartrate.some((v) => v !== null && v !== undefined);
  const hasPower = s.watts.some((v) => v !== null && v !== undefined);
  const hasPace = s.pace_s_per_km.some((v) => v !== null && v !== undefined);
  const hasAltitude = s.altitude.some((v) => v !== null && v !== undefined);

  const labels = s.time_s.map((t) => formatDuration(t));

  const datasets: Array<Record<string, unknown>> = [];
  if (hasHr) {
    datasets.push({
      label: "HR (bpm)",
      data: s.heartrate,
      borderColor: SERIES.heartRate,
      backgroundColor: "transparent",
      yAxisID: "hr",
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.3,
      spanGaps: true,
    });
  }
  if (hasPower) {
    datasets.push({
      label: "Power (W)",
      data: s.watts,
      borderColor: THEME.ink,
      backgroundColor: "transparent",
      yAxisID: "power",
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.3,
      spanGaps: true,
    });
  }
  if (hasPace) {
    // Pace: lower is faster → use inverse in plotting by simply passing s/km; user reads.
    datasets.push({
      label: "Pace (s/km)",
      data: s.pace_s_per_km,
      borderColor: SERIES.pace,
      backgroundColor: "transparent",
      yAxisID: "pace",
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.3,
      spanGaps: true,
      hidden: hasHr && hasPower, // hide by default when HR+Power already dense
    });
  }
  if (hasAltitude) {
    datasets.push({
      label: t("workout_detail.altitude_m_axis"),
      data: s.altitude,
      borderColor: SERIES.altitude,
      backgroundColor: SERIES.altitudeFill,
      yAxisID: "altitude",
      pointRadius: 0,
      borderWidth: 1,
      fill: true,
      tension: 0.3,
      spanGaps: true,
      hidden: true,
    });
  }

  return (
    <Card>
      <SectionLabel>{t("workout_detail.dynamics")}</SectionLabel>
      <div className="mt-3 h-[260px] md:h-[320px]">
        <Line
          data={{ labels, datasets } as unknown as Parameters<typeof Line>[0]["data"]}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            plugins: {
              legend: { position: "top", labels: { boxWidth: 12, font: { size: 11 } } },
              tooltip: { enabled: true },
            },
            scales: {
              x: {
                ticks: { maxTicksLimit: 8, font: { size: 10 } },
                grid: { color: "#E8E1D3" },
              },
              hr: {
                type: "linear",
                position: "left",
                display: hasHr,
                ticks: { font: { size: 10 } },
                grid: { color: "#E8E1D3" },
              },
              power: {
                type: "linear",
                position: "right",
                display: hasPower,
                grid: { display: false },
                ticks: { font: { size: 10 } },
              },
              pace: {
                type: "linear",
                position: "right",
                display: false,
                reverse: true,
              },
              altitude: {
                type: "linear",
                position: "right",
                display: false,
              },
            },
          }}
        />
      </div>
      <div className="mt-2 text-[11px] text-taupe">
        {t("workout_detail.samples_inline", { src: s.sample_count, drawn: s.time_s.length })}
      </div>
    </Card>
  );
}

// ============================================================================
// Laps / intervals table
// ============================================================================

function LapsTable({
  laps,
  label,
  sport,
}: {
  laps: WorkoutLap[];
  label: string;
  sport: string;
}) {
  const { t } = useTranslation();
  const showPower = laps.some((l) => l.average_watts !== null && l.average_watts !== undefined);
  const showPace = laps.some((l) => l.average_pace_s_per_km !== null && l.average_pace_s_per_km !== undefined);

  return (
    <Card>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="border-b border-cream-border text-left">
            <tr className="text-[10px] uppercase tracking-wider text-taupe">
              <th className="pb-2 pr-3">#</th>
              <th className="pb-2 pr-3">{t("workout_detail.type_col")}</th>
              <th className="pb-2 pr-3">{t("workout_detail.time_col")}</th>
              <th className="pb-2 pr-3">{t("workout_detail.distance_col")}</th>
              {showPace ? <th className="pb-2 pr-3">{t("workout_detail.pace")}</th> : null}
              <th className="pb-2 pr-3">HR</th>
              {showPower ? <th className="pb-2 pr-3">Power</th> : null}
              <th className="pb-2">Zone</th>
            </tr>
          </thead>
          <tbody>
            {laps.map((lap) => (
              <tr
                key={lap.index}
                className="border-b border-cream-border/50 last:border-0"
              >
                <td className="py-2 pr-3 text-taupe">{lap.index + 1}</td>
                <td className="py-2 pr-3 text-[12px] text-navy-deep">
                  {lap.type || lap.name || "—"}
                </td>
                <td className="metric-number py-2 pr-3">
                  {formatDuration(lap.moving_time_s)}
                </td>
                <td className="metric-number py-2 pr-3">
                  {formatDistance(lap.distance_m)}
                </td>
                {showPace ? (
                  <td className="metric-number py-2 pr-3">
                    {formatPace(lap.average_pace_s_per_km)}
                  </td>
                ) : null}
                <td className="metric-number py-2 pr-3">
                  {lap.average_heartrate !== null ? lap.average_heartrate : "—"}
                </td>
                {showPower ? (
                  <td className="metric-number py-2 pr-3">
                    {lap.average_watts !== null
                      ? `${Math.round(lap.average_watts)}W`
                      : "—"}
                  </td>
                ) : null}
                <td className="py-2">
                  {lap.zone ? (
                    <span
                      className="inline-flex h-5 items-center rounded-full px-2 text-[11px] font-bold"
                      style={(() => {
                        const bg = zoneColor(
                          parseInt(lap.zone.replace(/\D/g, "") || "0", 10) || 0,
                        );
                        return { background: bg, color: inkOn(bg) };
                      })()}
                    >
                      {lap.zone}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-taupe">
        {sport === "run" ? `${t("workout_detail.auto_laps_run")} ` : ""}
        {t("workout_detail.n_records", { n: laps.length })}
      </div>
    </Card>
  );
}

// ============================================================================
// HR / Power zone breakdown
// ============================================================================

function ZoneBar({
  zones,
  colorFor,
  formatUpperBound,
}: {
  zones: Array<{ zone: number; time_s: number; upperLabel: string | null }>;
  colorFor: (z: number) => string;
  formatUpperBound: (ub: number | null) => string;
}) {
  const total = zones.reduce((s, z) => s + (z.time_s || 0), 0) || 1;
  return (
    <div className="space-y-2">
      {zones.map((z) => {
        const pct = (z.time_s / total) * 100;
        return (
          <div key={z.zone}>
            <div className="mb-1 flex justify-between text-[11px]">
              <span className="font-medium">
                Z{z.zone}
                <span className="ml-1.5 text-taupe">{zoneLabel(z.zone)}</span>
                {z.upperLabel ? (
                  <span className="ml-1.5 text-taupe">
                    ≤ {z.upperLabel}
                  </span>
                ) : null}
              </span>
              <span className="text-taupe">
                <span className="metric-number text-navy-deep">{formatDuration(z.time_s)}</span>
                <span className="ml-2">{pct.toFixed(0)}%</span>
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cream-border">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, background: colorFor(z.zone) }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HrZonesCard({ zones }: { zones: HrZoneBin[] }) {
  const { t } = useTranslation();
  const total = totalZoneTime(zones);
  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <SectionLabel>{t("workout_detail.hr_zones")}</SectionLabel>
        <span className="text-[11px] text-taupe">{formatDuration(total)}</span>
      </div>
      <div className="mt-3">
        <ZoneBar
          zones={zones.map((z) => ({
            zone: z.zone,
            time_s: z.time_s,
            upperLabel: z.upper_bound_bpm !== null ? `${z.upper_bound_bpm} bpm` : null,
          }))}
          colorFor={zoneColor}
          formatUpperBound={(ub) => (ub !== null ? `${ub} bpm` : "")}
        />
      </div>
    </Card>
  );
}

function PowerZonesCard({ zones }: { zones: PowerZoneBin[] }) {
  const { t } = useTranslation();
  const total = totalZoneTime(zones);
  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <SectionLabel>{t("workout_detail.power_zones")}</SectionLabel>
        <span className="text-[11px] text-taupe">{formatDuration(total)}</span>
      </div>
      <div className="mt-3">
        <ZoneBar
          zones={zones.map((z) => ({
            zone: z.zone,
            time_s: z.time_s,
            upperLabel: z.upper_bound_watts !== null ? `${z.upper_bound_watts} W` : null,
          }))}
          colorFor={zoneColor}
          formatUpperBound={(ub) => (ub !== null ? `${ub} W` : "")}
        />
      </div>
    </Card>
  );
}

// ============================================================================
// Route map (Leaflet)
// ============================================================================

function FitBounds({ bounds }: { bounds: LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [16, 16] });
  }, [map, bounds]);
  return null;
}

function RouteMap({ latlng }: { latlng: number[][] }) {
  const { t } = useTranslation();
  const bounds = computeBounds(latlng);
  const start = latlng[0];
  const end = latlng[latlng.length - 1];
  return (
    <Card>
      <SectionLabel>
        <MapPin className="mr-1 inline h-3 w-3" strokeWidth={2} /> {t("workout_detail.route")}
      </SectionLabel>
      <div className="mt-3 h-[320px] overflow-hidden rounded-xl md:h-[420px]">
        <MapContainer
          bounds={bounds}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='© <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Polyline
            positions={latlng as [number, number][]}
            pathOptions={{ color: MAP.route, weight: 4, opacity: 0.9 }}
          />
          {start ? (
            <CircleMarker
              center={start as [number, number]}
              pathOptions={{ color: MAP.markerBorder, fillColor: MAP.start, fillOpacity: 1 }}
              radius={6}
            />
          ) : null}
          {end ? (
            <CircleMarker
              center={end as [number, number]}
              pathOptions={{ color: MAP.markerBorder, fillColor: MAP.finish, fillOpacity: 1 }}
              radius={6}
            />
          ) : null}
          <FitBounds bounds={bounds} />
        </MapContainer>
      </div>
    </Card>
  );
}

// ============================================================================
// Analysis — plain-language commentary on the session
// ============================================================================

function AnalysisCard({ workout }: { workout: WorkoutDetailType }) {
  const { t } = useTranslation();
  const bullets: Array<{ tone: "amber" | "coral" | "green"; text: React.ReactNode }> = [];

  if (workout.intensity_factor_pct !== null && workout.intensity_factor_pct >= 100) {
    bullets.push({
      tone: "coral",
      text: (
        <>
          <span className="font-medium">{t("workout_detail.high_intensity_label")}</span> IF ={" "}
          <span className="metric-number">{Math.round(workout.intensity_factor_pct)}%</span>. {t("workout_detail.high_intensity_desc")}
        </>
      ),
    });
  } else if (
    workout.intensity_factor_pct !== null &&
    workout.intensity_factor_pct >= 85
  ) {
    bullets.push({
      tone: "amber",
      text: (
        <>
          <span className="font-medium">{t("workout_detail.tempo_load_label")}</span> IF ={" "}
          <span className="metric-number">{Math.round(workout.intensity_factor_pct)}%</span>.
        </>
      ),
    });
  }

  if (workout.hr_zones.length > 0) {
    const z5Plus = computeZ5PlusTime(workout.hr_zones);
    if (z5Plus > 600) {
      bullets.push({
        tone: "amber",
        text: (
          <>
            <span className="font-medium">{t("workout_detail.z5_plus_label")}</span>{" "}
            {formatDuration(z5Plus)}{t("workout_detail.z5_plus_desc")}
          </>
        ),
      });
    }
    const z1z2 =
      (workout.hr_zones.find((z) => z.zone === 1)?.time_s || 0) +
      (workout.hr_zones.find((z) => z.zone === 2)?.time_s || 0);
    const total = totalZoneTime(workout.hr_zones);
    if (total > 0 && z1z2 / total >= 0.8) {
      bullets.push({
        tone: "green",
        text: (
          <>
            <span className="font-medium">{t("workout_detail.aerobic_base_label")}</span>{" "}
            {Math.round((z1z2 / total) * 100)}{t("workout_detail.aerobic_base_desc")}
          </>
        ),
      });
    }
  }

  if (workout.tss !== null && workout.tss >= 120) {
    bullets.push({
      tone: "coral",
      text: (
        <>
          <span className="font-medium">{t("workout_detail.big_load_label")}</span> TSS{" "}
          {Math.round(workout.tss)}{t("workout_detail.big_load_desc")}
        </>
      ),
    });
  }

  if (workout.feel !== null) {
    const lbl = feelLabel(workout.feel);
    if (lbl) {
      bullets.push({
        tone: workout.feel >= 4 ? "green" : workout.feel <= 2 ? "coral" : "amber",
        text: (
          <>
            <span className="font-medium">{t("workout_detail.feel_label")}</span> {lbl} ({workout.feel}/5).
          </>
        ),
      });
    }
  }

  if (bullets.length === 0) {
    bullets.push({
      tone: "green",
      text: (
        <>
          {t("workout_detail.normal_workout")}
        </>
      ),
    });
  }

  return (
    <Card>
      <SectionLabel>{t("workout_detail.analysis")}</SectionLabel>
      <ul className="mt-3 space-y-2.5 text-[14px]">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2.5">
            <span
              className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
              style={{
                background:
                  b.tone === "coral"
                    ? THEME.danger
                    : b.tone === "amber"
                      ? THEME.accentDark
                      : THEME.mutedLight,
              }}
            />
            <span>{b.text}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ============================================================================
// Meta row — weather + device
// ============================================================================

function MetaRow({ workout }: { workout: WorkoutDetailType }) {
  const { t } = useTranslation();
  const w = workout.weather;
  const hasWeather = w && (w.temperature_c !== null || w.wind_speed_ms !== null);
  const hasDevice = workout.device_name;
  if (!hasWeather && !hasDevice) return null;

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {hasWeather ? (
        <Card>
          <SectionLabel>{t("workout_detail.weather")}</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
            {w.temperature_c !== null ? (
              <span className="inline-flex items-center gap-1.5">
                <Thermometer className="h-3.5 w-3.5 text-taupe" strokeWidth={1.8} />
                <span className="metric-number text-[15px]">
                  {w.temperature_c.toFixed(1)}°C
                </span>
              </span>
            ) : null}
            {w.wind_speed_ms !== null ? (
              <span className="inline-flex items-center gap-1.5">
                <Wind className="h-3.5 w-3.5 text-taupe" strokeWidth={1.8} />
                <span className="metric-number text-[15px]">
                  {w.wind_speed_ms.toFixed(1)} {t("workout_detail.wind_ms")}
                </span>
              </span>
            ) : null}
            {w.conditions ? (
              <span className="text-taupe">{w.conditions}</span>
            ) : null}
          </div>
        </Card>
      ) : null}
      {hasDevice ? (
        <Card>
          <SectionLabel>{t("workout_detail.device")}</SectionLabel>
          <div className="mt-3 flex items-center gap-2 text-[13px]">
            <Battery className="h-3.5 w-3.5 text-taupe" strokeWidth={1.8} />
            <DeviceAttribution deviceName={workout.device_name} />
          </div>
        </Card>
      ) : null}
    </div>
  );
}
